// src/components/workflow/ModalRevisionDocumento.jsx - CORREGIDO con Material-UI
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  AlertTitle,
  Paper,
  Grid,
  Chip,
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Description as DocumentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import firebaseStorageService from '../../services/firebaseStorage';

const ModalRevisionDocumento = ({ 
  documento, 
  onFinalizarRevision, 
  onCancelar, 
  cargando = false 
}) => {
  const [decision, setDecision] = useState('');
  const [comentario, setComentario] = useState('');
  const [error, setError] = useState('');
  const [descargando, setDescargando] = useState(false);

  // Opciones de decisión con estados reales
  const opciones = [
    { 
      valor: 4, 
      etiqueta: 'Aprobar', 
      color: 'success', 
      icono: CheckCircleIcon,
      descripcion: 'Documento cumple con todos los requisitos' 
    },
    { 
      valor: 5, 
      etiqueta: 'Rechazar', 
      color: 'error', 
      icono: ErrorIcon,
      descripcion: 'Documento no cumple con los requisitos - rechazo definitivo' 
    },
    { 
      valor: 7, 
      etiqueta: 'Requiere Corrección', 
      color: 'warning', 
      icono: WarningIcon,
      descripcion: 'Documento necesita correcciones - debe ser resubido' 
    }
  ];

  const handleSubmit = async () => {
    if (!decision) {
      setError('Debe seleccionar una decisión de revisión');
      return;
    }

    if (!comentario.trim()) {
      setError('Debe agregar un comentario explicando su decisión');
      return;
    }

    try {
      await onFinalizarRevision({
        estado: parseInt(decision),
        comentario: comentario.trim()
      });
      setError('');
    } catch (error) {
      console.error('Error procesando decisión:', error);
      setError('Error al procesar la decisión de revisión');
    }
  };

  const handleDescargar = async () => {
    if (documento?.urlDescarga) {
      window.open(documento.urlDescarga, '_blank');
    } else if (documento?.rutaArchivo) {
      setDescargando(true);
      try {
        const url = await firebaseStorageService.obtenerUrlDescarga(documento.rutaArchivo);
        window.open(url, '_blank');
      } catch (error) {
        console.error('Error descargando archivo:', error);
        setError('Error al descargar el archivo');
      } finally {
        setDescargando(false);
      }
    } else {
      setError('No hay archivo disponible para descargar');
    }
  };

  const formatearTamaño = (bytes) => {
    if (!bytes) return '';
    return firebaseStorageService.formatearTamaño(bytes);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEstadoChip = (estadoId) => {
    const estados = {
      1: { label: 'Pendiente', color: 'default' },
      2: { label: 'Radicado', color: 'primary' },
      3: { label: 'En Revisión', color: 'warning' },
      4: { label: 'Aprobado', color: 'success' },
      5: { label: 'Rechazado', color: 'error' },
      6: { label: 'Requiere Corrección', color: 'warning' },
      7: { label: 'Pendiente Recarga', color: 'error' },
      8: { label: 'Subido', color: 'info' }
    };
    
    const estado = estados[estadoId] || { label: 'Desconocido', color: 'default' };
    return <Chip label={estado.label} color={estado.color} size="small" />;
  };

  if (!documento) {
    return null;
  }

  return (
    <Dialog 
      open={true} 
      onClose={onCancelar}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocumentIcon color="primary" />
            <Box>
              <Typography variant="h6" component="div">
                Revisar Documento
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {documento.nombreTipoDocumento || documento.tipoDocumento}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onCancelar}
            disabled={cargando}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {/* Información del documento */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
            📋 Información del Documento
          </Typography>
          
          {/* Archivo */}
          {documento.nombreArchivo && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DocumentIcon color="info" />
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {documento.nombreArchivo}
                  </Typography>
                </Box>
                <Button
                  onClick={handleDescargar}
                  disabled={descargando || cargando}
                  variant="contained"
                  size="small"
                  startIcon={descargando ? <CircularProgress size={16} /> : <DownloadIcon />}
                >
                  {descargando ? 'Descargando...' : 'Descargar y Revisar'}
                </Button>
              </Box>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tamaño:</strong> {formatearTamaño(documento.tamanoBytes)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Subido:</strong> {formatearFecha(documento.fechaSubida)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Usuario:</strong> {documento.usuarioSubida || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Radicado:</strong> {documento.radicado || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Detalles técnicos */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Estado Actual:</strong>
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {getEstadoChip(documento.estadoDocumentoId)}
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Entidad ID:</strong> {documento.entidadId}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Workflow ID:</strong> {documento.workflowNegocioId}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                <strong>Obligatorio:</strong> {documento.esObligatorio ? 'Sí' : 'No'}
              </Typography>
            </Grid>
          </Grid>

          {documento.descripcionTipoDocumento && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
              <Typography variant="body2" color="info.main">
                <strong>Descripción:</strong> {documento.descripcionTipoDocumento}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Alerta de error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}

        {/* Instrucciones */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Instrucciones de Revisión</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            1. <strong>Descargue y revise</strong> el documento adjunto
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            2. <strong>Seleccione una decisión</strong> según el cumplimiento de requisitos
          </Typography>
          <Typography variant="body2">
            3. <strong>Agregue comentarios detallados</strong> para justificar su decisión
          </Typography>
        </Alert>

        <Divider sx={{ my: 3 }} />

        {/* Formulario de decisión */}
        <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
          <FormLabel component="legend" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 2 }}>
            Decisión de Revisión *
          </FormLabel>
          <RadioGroup
            value={decision}
            onChange={(e) => {
              setDecision(e.target.value);
              setError('');
            }}
          >
            {opciones.map((opcion) => {
              const IconoComponent = opcion.icono;
              return (
                <Paper 
                  key={opcion.valor} 
                  elevation={decision === opcion.valor.toString() ? 2 : 0}
                  sx={{ 
                    mb: 2, 
                    border: '1px solid', 
                    borderColor: decision === opcion.valor.toString() ? `${opcion.color}.main` : 'grey.300',
                    bgcolor: decision === opcion.valor.toString() ? `${opcion.color}.50` : 'transparent'
                  }}
                >
                  <FormControlLabel
                    value={opcion.valor.toString()}
                    control={<Radio color={opcion.color} />}
                    disabled={cargando}
                    sx={{ 
                      m: 0, 
                      p: 2, 
                      width: '100%',
                      alignItems: 'flex-start'
                    }}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ml: 1 }}>
                        <IconoComponent 
                          color={opcion.color} 
                          sx={{ mt: 0.5 }} 
                        />
                        <Box>
                          <Typography 
                            variant="subtitle2" 
                            sx={{ fontWeight: 'bold', color: `${opcion.color}.main` }}
                          >
                            {opcion.etiqueta}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {opcion.descripcion}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </Paper>
              );
            })}
          </RadioGroup>
        </FormControl>

        {/* Campo de comentario */}
        <TextField
          label="Comentario de Revisión"
          multiline
          rows={4}
          fullWidth
          required
          value={comentario}
          onChange={(e) => {
            setComentario(e.target.value);
            setError('');
          }}
          placeholder="Escriba sus observaciones detalladas sobre el documento..."
          helperText="Este comentario será visible para el usuario que subió el documento"
          disabled={cargando}
          sx={{ mb: 2 }}
          error={!!error && !comentario.trim()}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, bgcolor: 'grey.50' }}>
        <Button
          onClick={onCancelar}
          disabled={cargando}
          color="inherit"
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={cargando || !decision || !comentario.trim()}
          variant="contained"
          color="primary"
          startIcon={cargando ? <CircularProgress size={20} /> : null}
        >
          {cargando ? 'Procesando...' : 'Finalizar Revisión'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalRevisionDocumento;